import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AuthStore,
  DevicesList,
  PageWrapper,
  UnsavedChangesGuardedComponent,
  useDeviceStore,
  UserRole
} from '@ottobock/remote-update/domain';
import { LogService } from '@ottobock/shared/domain';

import { DevicesTableComponent } from '../devices-table/devices-table.component';

@Component({
  selector: 'remote-update-feature-remote-update-dashboard-device',
  imports: [PageWrapper, DevicesTableComponent, MatButtonModule, ReactiveFormsModule],
  templateUrl: './device.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './device.css'
})
export class Device implements OnInit, UnsavedChangesGuardedComponent {
  private snackBar = inject(MatSnackBar);
  private logService = inject(LogService);
  private destroyRef = inject(DestroyRef);
  store = inject(useDeviceStore);
  authStore = inject(AuthStore);

  data = computed<DevicesList[]>(() => this.store.devices().data?.items ?? []);
  isDownloadingDeviceRecordsFile = computed(() => this.store.deviceUpdateRecordsFile().loading);
  loading = computed(() => this.store.devices().loading);
  hasError = computed(() => this.store.devices().error !== null);
  isUpdateManager = computed(() =>
    this.authStore
      .userRoles()
      .some(role => role === UserRole.UpdateManager || role === UserRole.AppDeveloper)
  );

  unsavedChangesMessage =
    'If you leave now, your progress will be lost and no export will be created.';

  get hasUnsavedChanges(): boolean {
    return this.isDownloadingDeviceRecordsFile();
  }

  onLeaveConfirm() {
    this.store.cancelDownloadDeviceUpdateRecordsFile();
  }

  emptyTitle = 'No devices found';
  emptyDescription =
    'There are no entries in the database. If searching, please use a product ID or wildcards (e.g., OB_* or *_SN_123*45).';

  defaultPageSize = 10;
  currentPageIndex = 0;
  currentPageSize = 10;
  paginator: MatPaginator | null = null;

  submitted = false;
  form = new FormGroup({
    search: new FormControl('', [Validators.required])
  });

  ngOnInit(): void {
    this.store.clear();
    this.form.reset();
    this.currentPageIndex = 0;
    this.currentPageSize = this.defaultPageSize;

    this.store.loadNextPage(1, this.defaultPageSize);

    this.form.controls.search.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (!value) {
          this.submitted = false;
          this.store.loadNextPage(1, this.currentPageSize, '');
          if (this.paginator) {
            this.paginator.firstPage();
          }
          this.currentPageIndex = 0;
        }
      });
  }

  onPageChange(event: { pageIndex: number; pageSize: number }) {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
  }

  onPaginatorReady(paginator: MatPaginator) {
    this.paginator = paginator;
  }
  onSearch(): void {
    this.submitted = true;
    if (
      this.form.valid &&
      this.form.value.search !== null &&
      this.form.value.search !== undefined
    ) {
      const value = this.form.value.search;

      this.store.loadNextPage(1, this.currentPageSize, value);
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.currentPageIndex = 0;
    }
  }

  async onExportButtonClick() {
    try {
      await this.store.downloadDeviceUpdateRecordsFile();
    } catch (error) {
      this.logService.error('Error occurred while downloading the device records file', error);
      this.snackBar.open('Something went wrong. Try again.', 'Close');
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: BeforeUnloadEvent) {
    if (this.isDownloadingDeviceRecordsFile()) event.preventDefault();
  }
}
